import React from 'react';
import PropTypes from 'prop-types';
import {FmlxBanner, FmlxButton, FmlxLink, FmlxModal} from 'fmlx-common-ui';
import {useIntl} from 'react-intl';
import './CSVImportInvalidCSVModal.scss';
import FormattedHTMLMessage from 'components/FormattedHTMLMessage';
import {CSVImportTemplateColumns, FastRobotErrorcode} from 'constants/dataEnum';
import {useDispatch} from 'react-redux';
import {getCSVErrorInfoDetail} from 'stores/business/taskListPicklistBusiness';
import {FAST_CSV_IMPORT_WEB_HELP_URL} from 'constants/appConstant';

const getCsvErrorEntriesText = (errorEntries) => {
  if (!errorEntries?.length) {
    return '';
  }

  return errorEntries
    .map((entry) => {
      const {csvRow, csvColumn} = entry;
      const columnNumber = CSVImportTemplateColumns.findIndex((column) => column === csvColumn) + 1;
      return `${csvRow} (column ${columnNumber})`;
    })
    .join(', ');
};

function CSVErrorLinesInfo({errorName, message, errorEntries}) {
  return (
    <div className="csv-error-lines-info">
      <div className="csv-error-lines-info__error-type">{errorName}</div>
      {message ? <div className="csv-error-lines-info__error-detail">{message}</div> : null}
      {errorEntries?.length ? (
        <div className="csv-error-lines-info__error-csv-lines">Line: {getCsvErrorEntriesText(errorEntries)}</div>
      ) : null}
    </div>
  );
}

CSVErrorLinesInfo.propTypes = {
  errorName: PropTypes.string.isRequired,
  message: PropTypes.string.isRequired,
  errorEntries: PropTypes.array.isRequired,
};

function CSVImportInvalidCSVModal(props) {
  const {isLoadingUploadCsv, onAbort, errorData, onOpenFileInput} = props;
  const intl = useIntl();
  const dispatch = useDispatch();

  const {errorNumber, errorCode, errorDetail} = errorData;

  const getInvalidColumnContent = () => (
    <div className="csv-import-invalid-csv-modal-content__section">
      {intl.formatMessage({id: 'modal.csvImportInvalidCSV.invalidColumn.message'})}
    </div>
  );

  const getErrorLinesInfoList = () => {
    // v1 csv don't have error detail
    // v2 csv only return errorCode: invalidCsvColumn and invalidCsvRow
    const isV1ErrorDetail = !errorDetail?.csvImportErrorCodes;

    if (isV1ErrorDetail) {
      const v1ErrorInfoDetail = dispatch(getCSVErrorInfoDetail(errorCode));
      const {errorSubTitle, errorMessage} = v1ErrorInfoDetail;
      return [
        {
          errorName: errorSubTitle,
          message: errorMessage,
          csvLines: [],
        },
      ];
    }

    const {csvImportErrorCodes} = errorDetail;

    return Object.keys(csvImportErrorCodes).map((subErrorCode) => {
      const errorInfoDetail = dispatch(getCSVErrorInfoDetail(subErrorCode));
      const {errorSubTitle, errorMessage} = errorInfoDetail;

      return {
        errorName: errorSubTitle,
        message: errorMessage,
        errorEntries: csvImportErrorCodes[subErrorCode],
      };
    });
  };

  const getInvalidRowContent = () => {
    const errorLinesInfoList = getErrorLinesInfoList();

    return (
      <>
        <div className="csv-import-invalid-csv-modal-content__section">
          {intl.formatMessage({id: 'modal.csvImportInvalidCSV.invalidEntries.message'})}
        </div>
        <div className="csv-import-invalid-csv-modal-content__section">
          <div className="csv-import-modal-info-box">
            {errorLinesInfoList.map((errorInfo) => (
              <CSVErrorLinesInfo
                key={errorInfo.errorName}
                errorName={errorInfo.errorName}
                message={errorInfo.message}
                errorEntries={errorInfo.errorEntries}
              />
            ))}
          </div>
        </div>
      </>
    );
  };

  const getDetailInfo = () => {
    switch (errorCode) {
      case FastRobotErrorcode.INVALID_CSV_COLUMN:
        return getInvalidColumnContent();
      case FastRobotErrorcode.INVALID_CSV_ROW:
      default:
        return getInvalidRowContent();
    }
  };

  return (
    <FmlxModal
      open
      size={errorCode === FastRobotErrorcode.INVALID_CSV_COLUMN ? 'md' : 'lg'}
      title={intl.formatMessage({id: 'modal.csvImportInvalidCSV.title'})}
      hideCloseButton
      tertiaryButton={{show: false}}
      customFooter={
        <div className="csv-import-invalid-csv-modal-footer">
          <FmlxButton
            id="modal.csvImportInvalidCSV.button.reupload"
            label={
              isLoadingUploadCsv
                ? intl.formatMessage({id: 'modal.csvImportFileUpload.uploading'})
                : intl.formatMessage({id: 'modal.csvImportInvalidCSV.button.reupload'})
            }
            type="primary"
            variant="contain"
            showProgress={isLoadingUploadCsv}
            onClick={onOpenFileInput}
          />
          <FmlxButton
            id="modal.csvImportInvalidCSV.button.abort"
            label={intl.formatMessage({id: 'modal.csvImportInvalidCSV.button.abort'})}
            type="danger"
            variant="outline"
            onClick={onAbort}
          />
        </div>
      }
    >
      <div className="csv-import-invalid-csv-modal-content">
        <div className="csv-import-invalid-csv-modal-content__section">
          <FmlxBanner
            size="sm"
            variant="error"
            id="modal.csvImportInvalidCSV.alert.error"
            title={
              <span style={{color: '#DB0000'}}>
                <FormattedHTMLMessage id="modal.csvImportInvalidCSV.alert.error" values={{errorCode: errorNumber}} />
              </span>
            }
            hideCloseButton
          />
        </div>

        {getDetailInfo()}

        <div className="csv-import-invalid-csv-modal-content__section">
          <FormattedHTMLMessage
            id="modal.csvImportInvalidCSV.message.learnMore"
            values={{
              link: (
                <FmlxLink
                  id="modal.csvImportInvalidCSV.link.learnMore"
                  label={intl.formatMessage({id: 'modal.csvImportInvalidCSV.link.learnMore'})}
                  // TODO: change url to specific csv import section after stable release
                  href={FAST_CSV_IMPORT_WEB_HELP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                />
              ),
            }}
          />
        </div>
      </div>
    </FmlxModal>
  );
}

CSVImportInvalidCSVModal.propTypes = {
  errorData: PropTypes.object.isRequired,
  onOpenFileInput: PropTypes.func.isRequired,
  isLoadingUploadCsv: PropTypes.bool,
  onAbort: PropTypes.func,
};

CSVImportInvalidCSVModal.defaultProps = {
  onAbort: () => {},
  isLoadingUploadCsv: false,
};

export default CSVImportInvalidCSVModal;
